import '@/shared/lib/i18n'; // Initialize i18n before app renders
import { ensureFreshClient } from '@/shared/lib/buildVersion'
import { branding, injectTheme, injectFont } from './config'
import * as React from 'react';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import App from './App.tsx';
import './index.css';

// Sentry initialization (before any other app code)
const sentryDSN = import.meta.env.VITE_SENTRY_DSN;
const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT;
const forceEnable = import.meta.env.VITE_SENTRY_FORCE_ENABLE === 'true';
const envEnabled = import.meta.env.VITE_SENTRY_ENABLED !== 'false';
const hasDsn = Boolean(sentryDSN);
const enabled = hasDsn && envEnabled && (forceEnable || environment !== 'development');
const debug = import.meta.env.VITE_SENTRY_DEBUG === 'true';

// Derive the Keycloak hostname from VITE_KEYCLOAK_URL to exclude it from Sentry
// trace propagation. Keycloak rejects the sentry-trace header via CORS, which
// breaks token refresh requests when Sentry is active (e.g. in production).
const keycloakHostname = (() => {
  try {
    return new URL(import.meta.env.VITE_KEYCLOAK_URL ?? '').hostname;
  } catch {
    return '';
  }
})();

// Match *.visagio.com but exclude the Keycloak host so Sentry headers are never
// injected into auth requests (which would be blocked by the CORS preflight).
const keycloakEscaped = keycloakHostname.replace(/\./g, '\\.');
const visagioTracingTarget = keycloakHostname
  ? new RegExp(`^https://(?!${keycloakEscaped}).*\\.visagio\\.com`)
  : /^\/\/.*\.visagio\.com/;

if (enabled) {
  Sentry.init({
    enabled,
    dsn: sentryDSN,
    debug,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
        // Skip auth requests – Keycloak rejects the sentry-trace header via CORS.
        shouldCreateSpanForRequest: (url: string) => {
          if (keycloakHostname && url.includes(keycloakHostname)) return false;
          return true;
        },
      }),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration(),
      Sentry.captureConsoleIntegration({
        levels: ['error'],
      }),
    ],
    environment,
    tracesSampleRate: 0.1,
    profilesSampleRate: 0.1,
    tracePropagationTargets: [
      'localhost',
      // Propagate headers only to the backend API, never to the Keycloak host.
      // The excluded hostname is derived from VITE_KEYCLOAK_URL at startup.
      visagioTracingTarget,
    ],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
  });
}

// Custom font from env (VITE_FONT_FAMILY, VITE_FONT_URL)
injectFont()
// Apply branding from env (title and favicon)
document.title = branding.tabSuffix ? `${branding.appTitle} | ${branding.tabSuffix}` : branding.appTitle
// Set favicon from JS so the correct path (and cache-bust in dev) is used before the browser requests it
const base = (import.meta.env.BASE_URL ?? '/').replace(/\/$/, '') || ''
const faviconPath = branding.faviconPath.startsWith('/') ? branding.faviconPath.slice(1) : branding.faviconPath
let faviconHref = `${base}/${faviconPath}`.replace(/\/+/g, '/')
if (import.meta.env.DEV) faviconHref += (faviconHref.includes('?') ? '&' : '?') + 't=' + Date.now()
let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
if (!link) {
  link = document.createElement('link')
  link.rel = 'icon'
  link.type = 'image/png'
  link.id = 'favicon-link'
  document.head.appendChild(link)
}
link.href = faviconHref

// Theme colors from env (VITE_THEME_*)
injectTheme()

ensureFreshClient().finally(() => {
  createRoot(document.getElementById("root")!).render(<App />);
});
